import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../common/Card';
import { prosthesisApi, patientService, treatmentsApi, consultationService } from '../../services/api';
import {
  CreateCommandeProtheseData,
  Prestataire,
  TypeProthese,
  Patient,
  Treatment,
  Consultation
} from '../../types';
import {
  Save,
  X,
  Calendar,
  User,
  Package,
  Building,
  DollarSign
} from 'lucide-react';
import PatientSearchSelect from '../common/PatientSearchSelect';

interface ProsthesisOrderFormProps {
  orderId?: number; // Si fourni, on charge et modifie cette commande
  onSubmit: (data: CreateCommandeProtheseData) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ProsthesisOrderForm: React.FC<ProsthesisOrderFormProps> = ({
  orderId,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<CreateCommandeProtheseData>({
    patient: 0,
    consultation: 0,
    prestataire: 0,
    type_prothese: 0,
    description: '',
    specifications: '',
    nombre_dents: 1,
    date_empreintes: '',
    date_livraison_prevue: '',
    cout_fabrication: 0,
    cout_transport: 0
  });

  const [prestataires, setPrestataires] = useState<Prestataire[]>([]);
  const [typesProtheses, setTypesProtheses] = useState<TypeProthese[]>([]);
  const [selectedPatientConsultations, setSelectedPatientConsultations] = useState<Consultation[]>([]);
  const [formLoading, setFormLoading] = useState(true);

  // Charger les données de la commande existante si orderId est fourni
  useEffect(() => {
    const loadExistingOrder = async () => {
      if (!orderId) return;

      setFormLoading(true);
      try {
        const response = await prosthesisApi.getCommande(orderId);
        const order = response.data;

        // Convertir les dates ISO en format YYYY-MM-DD pour les inputs
        const formatDateForInput = (dateString: string) => {
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        setFormData({
          patient: order.patient,
          consultation: order.consultation || 0,
          prestataire: order.prestataire,
          type_prothese: order.type_prothese,
          description: order.description,
          specifications: order.specifications || '',
          nombre_dents: order.nombre_dents || 1,
          date_empreintes: formatDateForInput(order.date_empreintes),
          date_livraison_prevue: formatDateForInput(order.date_livraison_prevue),
          cout_fabrication: order.cout_fabrication,
          cout_transport: order.cout_transport || 0
        });
      } catch (error) {
        console.error('Erreur lors du chargement de la commande:', error);
        alert('Erreur lors du chargement de la commande');
      }
    };

    loadExistingOrder();
  }, [orderId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          prestatairesResponse,
          typesResponse
        ] = await Promise.all([
          prosthesisApi.getPrestataires({ page_size: 100 }),
          prosthesisApi.getTypesProtheses({ page_size: 100 })
        ]);

        console.log('Prestataires response:', prestatairesResponse.data);
        console.log('Types protheses response:', typesResponse.data);

        setPrestataires(prestatairesResponse.data.results || []);
        setTypesProtheses(typesResponse.data.results || []);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as any;
          console.error('Response data:', axiosError.response?.data);
          console.error('Response status:', axiosError.response?.status);
        }
      } finally {
        setFormLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchPatientConsultations = async () => {
      if (formData.patient) {
        try {
          const response = await consultationService.getByPatient(formData.patient);
          const consultations = response.data.results || [];

          // Trier les consultations par date décroissante (plus récentes en premier)
          const sortedConsultations = consultations.sort((a, b) =>
            new Date(b.date_consultation).getTime() - new Date(a.date_consultation).getTime()
          );

          setSelectedPatientConsultations(sortedConsultations);

          // Sélectionner automatiquement la dernière consultation si elle existe
          if (sortedConsultations.length > 0 && !formData.consultation) {
            // Sélectionner automatiquement la dernière consultation
            const lastConsultation = sortedConsultations[0];
            handleInputChange('consultation', lastConsultation.id);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des consultations:', error);
        }
      } else {
        setSelectedPatientConsultations([]);
      }
    };

    fetchPatientConsultations();
  }, [formData.patient]);

  const handleInputChange = (field: keyof CreateCommandeProtheseData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const calculateDeliveryDate = (prestataireId: number, impressionDate: string) => {
    const prestataire = prestataires.find(p => p.id === prestataireId);
    if (prestataire && impressionDate) {
      const impression = new Date(impressionDate);
      impression.setDate(impression.getDate() + prestataire.delai_fabrication_moyen);
      return impression.toISOString().split('T')[0];
    }
    return '';
  };

  useEffect(() => {
    if (formData.prestataire && formData.date_empreintes) {
      const deliveryDate = calculateDeliveryDate(formData.prestataire, formData.date_empreintes);
      if (deliveryDate) {
        setFormData(prev => ({
          ...prev,
          date_livraison_prevue: deliveryDate
        }));
      }
    }
  }, [formData.prestataire, formData.date_empreintes, prestataires]);

  // Calculer automatiquement le coût de fabrication
  useEffect(() => {
    if (formData.type_prothese && formData.nombre_dents) {
      const typeProthese = typesProtheses.find(t => t.id === formData.type_prothese);
      if (typeProthese) {
        const coutCalcule = typeProthese.cout_base * (formData.nombre_dents || 1);
        setFormData(prev => ({
          ...prev,
          cout_fabrication: coutCalcule
        }));
      }
    }
  }, [formData.type_prothese, formData.nombre_dents, typesProtheses]);

  if (formLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  console.log('Render state:', {
    prestataires: prestataires.length,
    typesProtheses: typesProtheses.length,
    selectedPatientConsultations: selectedPatientConsultations.length
  });

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {orderId ? 'Modifier la Commande de Prothèse' : 'Nouvelle Commande de Prothèse'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sélection du patient */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="inline h-4 w-4 mr-1" />
                  Patient *
                </label>
                <PatientSearchSelect
                  value={formData.patient || undefined}
                  onChange={(patientId) => {
                    handleInputChange('patient', patientId || 0);
                  }}
                  placeholder="Rechercher un patient par nom, prénom ou téléphone..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Consultation *
                </label>
                <select
                  value={formData.consultation}
                  onChange={(e) => handleInputChange('consultation', parseInt(e.target.value))}
                  required
                  disabled={!formData.patient}
                  title="Sélectionner une consultation"
                  className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
                >
                  <option value="">Sélectionner une consultation</option>
                  {selectedPatientConsultations.map(consultation => (
                    <option key={consultation.id} value={consultation.id}>
                      {new Date(consultation.date_consultation).toLocaleDateString('fr-FR')} - {consultation.motif_consultation}
                      {consultation.diagnostic ? ` (${consultation.diagnostic})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Prestataire et type de prothèse */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="inline h-4 w-4 mr-1" />
                  Prestataire *
                </label>
                <select
                  value={formData.prestataire}
                  onChange={(e) => handleInputChange('prestataire', parseInt(e.target.value))}
                  required
                  title="Sélectionner un prestataire"
                  className="w-full px-3 py-2 border border-gray-300  text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Sélectionner un prestataire</option>
                  {prestataires.filter(p => p.is_active).map(prestataire => (
                    <option key={prestataire.id} value={prestataire.id}>
                      {prestataire.nom} ({prestataire.delai_fabrication_moyen} jours)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de prothèse *
                </label>
                <select
                  value={formData.type_prothese}
                  onChange={(e) => handleInputChange('type_prothese', parseInt(e.target.value))}
                  required
                  title="Sélectionner un type de prothèse"
                  className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Sélectionner un type</option>
                  {typesProtheses.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.nom} - {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'GNF' }).format(type.cout_base)} / dent
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Nombre de dents */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de dents *
                </label>
                <input
                  type="number"
                  min="1"
                  max="32"
                  value={formData.nombre_dents || 1}
                  onChange={(e) => handleInputChange('nombre_dents', parseInt(e.target.value) || 1)}
                  required
                  placeholder="Nombre de dents concernées"
                  title="Nombre de dents concernées par la prothèse"
                  className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Le prix est par dent. Le coût total sera calculé automatiquement.
                </p>
              </div>
              <div className="flex items-center">
                {formData.type_prothese && formData.nombre_dents && (
                  <div className="bg-blue-50 p-4 rounded-lg w-full">
                    <p className="text-sm font-medium text-gray-700 mb-1">Calcul du coût</p>
                    <p className="text-xs text-gray-600">
                      {typesProtheses.find(t => t.id === formData.type_prothese)?.nom}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'GNF' }).format(
                        typesProtheses.find(t => t.id === formData.type_prothese)?.cout_base || 0
                      )} × {formData.nombre_dents} dent(s)
                    </p>
                    <p className="text-lg font-semibold text-blue-600 mt-1">
                      = {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'GNF' }).format(formData.cout_fabrication)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Date des empreintes *
                </label>
                <input
                  type="date"
                  value={formData.date_empreintes}
                  onChange={(e) => handleInputChange('date_empreintes', e.target.value)}
                  required
                  placeholder="Sélectionner la date des empreintes"
                  title="Date des empreintes"
                  className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de livraison prévue *
                </label>
                <input
                  type="date"
                  value={formData.date_livraison_prevue}
                  onChange={(e) => handleInputChange('date_livraison_prevue', e.target.value)}
                  required
                  placeholder="Sélectionner la date de livraison prévue"
                  title="Date de livraison prévue"
                  className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>

            {/* Coûts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="inline h-4 w-4 mr-1" />
                  Coût de fabrication (GNF) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cout_fabrication}
                  onChange={(e) => handleInputChange('cout_fabrication', parseFloat(e.target.value) || 0)}
                  required
                  placeholder="Calculé automatiquement"
                  title="Coût de fabrication en GNF (calculé automatiquement)"
                  className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-gray-50"
                  readOnly={!!(formData.type_prothese && formData.nombre_dents)}
                />
                {formData.type_prothese && formData.nombre_dents && (
                  <p className="mt-1 text-sm text-blue-600">
                    ✓ Calculé automatiquement (modifiable si nécessaire)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coût de transport (GNF)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cout_transport || 0}
                  onChange={(e) => handleInputChange('cout_transport', parseFloat(e.target.value) || 0)}
                  placeholder="Entrez le coût de transport"
                  title="Coût de transport en GNF"
                  className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
                rows={3}
                placeholder="Description de la prothèse à fabriquer..."
                className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>

            {/* Spécifications */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Spécifications techniques
              </label>
              <textarea
                value={formData.specifications || ''}
                onChange={(e) => handleInputChange('specifications', e.target.value)}
                rows={3}
                placeholder="Spécifications techniques particulières..."
                className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>

            {/* Résumé des coûts */}
            {(formData.cout_fabrication > 0 || (formData.cout_transport && formData.cout_transport > 0)) && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Résumé des coûts</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Fabrication ({formData.nombre_dents || 1} dent{(formData.nombre_dents || 1) > 1 ? 's' : ''}):</span>
                    <span>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'GNF' }).format(formData.cout_fabrication)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Transport:</span>
                    <span>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'GNF' }).format(formData.cout_transport || 0)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-1">
                    <span>Total:</span>
                    <span>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'GNF' }).format(formData.cout_fabrication + (formData.cout_transport || 0))}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Boutons d'action */}
            <div className="flex justify-end gap-4 pt-6 border-t">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {loading ? (orderId ? 'Modification...' : 'Création...') : (orderId ? 'Modifier la commande' : 'Créer la commande')}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProsthesisOrderForm;